import type { CategoryCollection } from '../types/entities';
import type { useWorkspaceStore } from '../stores/workspace';
import { nextTick, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { EntitySummary } from '../types/entities';
import { templateInheritsFrom } from '../lib/templateInheritance';

export function useExplorerActions(options: {
  store: ReturnType<typeof useWorkspaceStore>;
  folder: { label: string; parentId: string | null };
  folderOpen: Ref<boolean>;
  draggedExplorerNodeId: Ref<string | null>;
  draggedCategoryEntityIds: Ref<string[]>;
  selectedCategoryEntityIds: Ref<string[]>;
  draggedCategoryId: Ref<string | null>;
  categoryMenuId: Ref<string | null>;
  explorerSearchOpen: Ref<boolean>;
  explorerSearchInput: Ref<HTMLInputElement | null>;
  selectEntity: (id: string) => Promise<void>;
}) {
  const { store, folder, folderOpen, draggedExplorerNodeId, draggedCategoryEntityIds, selectedCategoryEntityIds, draggedCategoryId, categoryMenuId, explorerSearchOpen, explorerSearchInput, selectEntity } = options;
  const { entities, templates, categories, categoryCollections } = storeToRefs(store);
async function submitFolder() {
  if (!folder.label.trim()) return;
  try {
    await store.createFolder(folder.label.trim(), folder.parentId);
    folder.label = ''; folder.parentId = null; folderOpen.value = false;
  } catch { return; }
}
async function moveExplorerNode(nodeId: string, parentId: string | null) {
  await store.updateExplorerNode(nodeId, { parentId }).catch(() => undefined);
}
function dragExplorerNode(nodeId: string) { draggedExplorerNodeId.value = nodeId; }
function toggleCategoryEntitySelection(entityId: string) {
  selectedCategoryEntityIds.value = selectedCategoryEntityIds.value.includes(entityId)
    ? selectedCategoryEntityIds.value.filter((id) => id !== entityId)
    : [...selectedCategoryEntityIds.value, entityId];
}
function openCategoryEntity(entityId: string) {
  // A normal click establishes the first item of a potential multi-selection.
  // Ctrl/Cmd clicks can then extend it before the drag starts.
  selectedCategoryEntityIds.value = [entityId];
  selectEntity(entityId);
}
function dragCategoryEntity(entityId: string) {
  draggedCategoryEntityIds.value = selectedCategoryEntityIds.value.includes(entityId)
    ? [...selectedCategoryEntityIds.value]
    : [entityId];
}
async function dropExplorerNode(targetNodeId: string) {
  if (!draggedExplorerNodeId.value || draggedExplorerNodeId.value === targetNodeId) return;
  await moveExplorerNode(draggedExplorerNodeId.value, targetNodeId);
  draggedExplorerNodeId.value = null;
}
async function dropCategoryEntity(targetNodeId: string) {
  const entityIds = draggedCategoryEntityIds.value;
  if (!entityIds.length) return;
  await Promise.all(entityIds.map((entityId) => store.addEntityToExplorer(entityId, targetNodeId).catch(() => undefined)));
  draggedCategoryEntityIds.value = [];
  selectedCategoryEntityIds.value = [];
}
async function dropCategoryEntityIntoCollection(collection: CategoryCollection) {
  const entityIds = draggedCategoryEntityIds.value;
  if (!entityIds.length) return;
  const moves = entityIds.map((entityId) => ({
    entity: entities.value.find((item) => item.id === entityId),
    previousCollections: categoryCollections.value.filter((item) => item.id !== collection.id && item.categoryKey === collection.categoryKey && item.entityIds.includes(entityId))
  })).filter((move): move is { entity: EntitySummary; previousCollections: CategoryCollection[] } => Boolean(move.entity)
    && move.entity!.templateCategory === collection.categoryKey
    && (!collection.templateId || templateInheritsFrom(move.entity!.templateId, collection.templateId, templates.value)));
  for (const move of moves) {
    try {
      await store.addEntityToCategoryCollection(collection.id, move.entity.id);
      await Promise.all(move.previousCollections.map((item) => store.removeEntityFromCategoryCollection(item.id, move.entity.id)));
    } catch {
      // Eine unpassende Entity bleibt unverändert; gültige Auswahl-Elemente werden weiter verarbeitet.
    }
  }
  draggedCategoryEntityIds.value = [];
  selectedCategoryEntityIds.value = [];
}
function toggleExplorerSearch() {
  explorerSearchOpen.value = !explorerSearchOpen.value;
  if (explorerSearchOpen.value) nextTick(() => explorerSearchInput.value?.focus());
}
async function removeCategoryEntityFromCollection(input: { collection: CategoryCollection; entityId: string }) {
  try {
    await store.removeEntityFromCategoryCollection(input.collection.id, input.entityId);
    selectedCategoryEntityIds.value = selectedCategoryEntityIds.value.filter((id) => id !== input.entityId);
  } catch {
    // Die Entity bleibt sichtbar, falls die Zuordnung inzwischen geändert wurde.
  }
}
async function dropCategoryEntityIntoCategory(categoryKey: string) {
  const entityIds = draggedCategoryEntityIds.value;
  if (!entityIds.length) return;
  const moves = entityIds.map((entityId) => ({
    entity: entities.value.find((item) => item.id === entityId),
    collections: categoryCollections.value.filter((item) => item.categoryKey === categoryKey && item.entityIds.includes(entityId))
  })).filter((move): move is { entity: EntitySummary; collections: CategoryCollection[] } => Boolean(move.entity) && move.entity!.templateCategory === categoryKey);
  for (const move of moves) await Promise.all(move.collections.map((collection) => store.removeEntityFromCategoryCollection(collection.id, move.entity.id)));
  draggedCategoryEntityIds.value = [];
  selectedCategoryEntityIds.value = [];
}
async function dropOntoCategory(category: typeof categories.value[number]) {
  if (draggedCategoryEntityIds.value.length) await dropCategoryEntityIntoCategory(category.key);
  else await dropCategory(category.id);
}
async function deleteCategoryCollectionFromExplorer(collection: CategoryCollection) {
  const message = `Sammlung „${collection.name}“ löschen? Enthaltene Entities bleiben erhalten und erscheinen wieder direkt in ihrer Kategorie.`;
  if (window.confirm(message)) await store.deleteCategoryCollection(collection.id, { mode: 'unassign' }).catch(() => undefined);
}
async function dropCategory(categoryId: string) {
  const current = categories.value.find((item) => item.id === draggedCategoryId.value);
  const target = categories.value.find((item) => item.id === categoryId);
  if (!current || !target || current.id === target.id) return;
  await store.updateCategory(current.id, { sortOrder: target.sortOrder });
  await store.updateCategory(target.id, { sortOrder: current.sortOrder });
  draggedCategoryId.value = null;
}
async function renameCategoryQuick(category: typeof categories.value[number]) {
  const name = window.prompt('Kategoriename', category.name);
  if (!name?.trim()) return;
  try { await store.updateCategory(category.id, { name: name.trim() }); categoryMenuId.value = null; }
  catch (error) { window.alert(error instanceof Error ? error.message : 'Kategorie konnte nicht gespeichert werden.'); }
}
async function setCategoryColorQuick(category: typeof categories.value[number], event: Event) {
  const color = (event.target as HTMLInputElement).value;
  try { await store.updateCategory(category.id, { color }); }
  catch (error) { window.alert(error instanceof Error ? error.message : 'Kategorie konnte nicht gespeichert werden.'); }
}
async function toggleCategoryExplorerQuick(category: typeof categories.value[number]) {
  try {
    await store.updateCategory(category.id, { showInExplorer: !category.showInExplorer });
    categoryMenuId.value = null;
  } catch (error) {
    window.alert(error instanceof Error ? error.message : 'Explorer-Sichtbarkeit konnte nicht gespeichert werden.');
  }
}
  return { submitFolder, moveExplorerNode, dragExplorerNode, toggleCategoryEntitySelection, openCategoryEntity, dragCategoryEntity, dropExplorerNode, dropCategoryEntity, dropCategoryEntityIntoCollection, toggleExplorerSearch, removeCategoryEntityFromCollection, dropOntoCategory, deleteCategoryCollectionFromExplorer, renameCategoryQuick, setCategoryColorQuick, toggleCategoryExplorerQuick };
}
